import { z } from 'zod';

/* ─────────── Уведомления: утренняя сводка на почту и в Telegram (раздел 6.20: УВ-05, УВ-07, УВ-09; ЗД-06, БЗ-04, ИН-13) ─────────── */

export const NOTIFICATION_CHANNELS = { email: 'Почта', telegram: 'Telegram' } as const;
export type NotificationChannel = keyof typeof NOTIFICATION_CHANNELS;
export const NotificationChannel = z.enum(['email', 'telegram']);

/** Час утренней сводки по часам компании: с 6:00 до 11:00 */
export const DIGEST_HOURS = [6, 7, 8, 9, 10, 11] as const;
export const DEFAULT_DIGEST_HOUR = 8;
/** Код привязки Telegram действует 15 минут */
export const TELEGRAM_CODE_TTL_MINUTES = 15;

/** Часовые пояса Казахстана; по умолчанию — единое время страны с 2024 года (UTC+5) */
export const TIMEZONES = {
  'Asia/Almaty': 'Казахстан (UTC+5)',
  'Asia/Aqtobe': 'Актобе (UTC+5)',
  'Asia/Atyrau': 'Атырау (UTC+5)',
  'Asia/Oral': 'Уральск (UTC+5)',
  'Asia/Qyzylorda': 'Кызылорда (UTC+5)',
  'Asia/Tashkent': 'Ташкент (UTC+5)',
  'Asia/Bishkek': 'Бишкек (UTC+6)',
  'Europe/Moscow': 'Москва (UTC+3)',
} as const;
export type Timezone = keyof typeof TIMEZONES;
export const Timezone = z.enum(Object.keys(TIMEZONES) as [Timezone, ...Timezone[]]);

/** Личные настройки уведомлений (УВ-07): каналы и утренняя сводка */
export const NotificationSettingsInput = z.object({
  /** Утренняя сводка моих дел (ЗД-06) */
  digestEnabled: z.boolean(),
  /** Час сводки по часам компании; null — как у компании */
  digestHour: z
    .union([z.literal(6), z.literal(7), z.literal(8), z.literal(9), z.literal(10), z.literal(11)])
    .nullable(),
  emailEnabled: z.boolean(),
  telegramEnabled: z.boolean(),
  /** Включать в сводку «Что нового» после выкладок (БЗ-04) */
  releasesEnabled: z.boolean(),
  /** Письмо «Просрочки команды» руководителю проекта (ЗД-13); не передано — не меняется */
  controlEnabled: z.boolean().optional(),
});
export type NotificationSettingsInput = z.infer<typeof NotificationSettingsInput>;

export const DeliveryStatus = z.enum(['sent', 'failed', 'skipped']);
export type DeliveryStatus = z.infer<typeof DeliveryStatus>;

/** Запись журнала доставки (УВ-09): кому, когда, по какому каналу, дошло ли */
export const DeliveryView = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  userName: z.string().nullable(),
  /**
   * alert — служебное сообщение: предупреждение о выкладке, сбой, письмо «Портал обновлён» (АД-09);
   * control — контроль сроков по команде руководителю проекта (ЗД-13)
   */
  kind: z.enum(['digest', 'test', 'alert', 'control']),
  channel: NotificationChannel,
  status: DeliveryStatus,
  /** Короткое описание ошибки без адресов и секретов */
  error: z.string().nullable(),
  itemsCount: z.number().int(),
  sentAt: z.string(),
});
export type DeliveryView = z.infer<typeof DeliveryView>;

/** Настройки в ответе: час хранится как число, даже если задан вне списка выбора */
export const NotificationSettingsView = NotificationSettingsInput.extend({
  digestHour: z.number().int().min(0).max(23).nullable(),
});
export type NotificationSettingsView = z.infer<typeof NotificationSettingsView>;

/** Что видит человек в профиле: настройки, состояние каналов и последние доставки ему */
export const MyNotifications = z.object({
  settings: NotificationSettingsView,
  /** Час и пояс компании, по которым планировщик шлёт сводку (первая компания человека), когда свой час не задан */
  companyDigestHour: z.number().int(),
  timezone: z.string(),
  companyName: z.string().nullable(),
  email: z.object({
    /** Почтовый сервер настроен администратором портала */
    configured: z.boolean(),
    address: z.string(),
  }),
  telegram: z.object({
    /** Бот настроен администратором портала */
    configured: z.boolean(),
    botUsername: z.string().nullable(),
    linked: z.boolean(),
    username: z.string().nullable(),
    linkedAt: z.string().nullable(),
  }),
  deliveries: z.array(DeliveryView),
});
export type MyNotifications = z.infer<typeof MyNotifications>;

/** Код привязки Telegram: ссылка на бота и код для ручного ввода */
export const TelegramLinkCode = z.object({
  code: z.string(),
  deepLink: z.string().nullable(),
  botUsername: z.string().nullable(),
  expiresAt: z.string(),
});
export type TelegramLinkCode = z.infer<typeof TelegramLinkCode>;

/** Итог пробной отправки по каналам */
export const TestSendResult = z.object({
  results: z.array(
    z.object({
      channel: NotificationChannel,
      status: DeliveryStatus,
      error: z.string().nullable(),
    }),
  ),
  itemsCount: z.number().int(),
});
export type TestSendResult = z.infer<typeof TestSendResult>;

/** Настройки уведомлений компании (АД-08): пояс и час сводки по умолчанию */
export const CompanyNotificationSettingsInput = z.object({
  timezone: Timezone,
  digestHour: z.union([z.literal(6), z.literal(7), z.literal(8), z.literal(9), z.literal(10), z.literal(11)]),
  /**
   * Утренняя сводка сотрудникам компании включена. Выключенная не уходит никому из компании — например, пока
   * портал запускается; пробную сводку из профиля по-прежнему можно прислать себе. Не задано — включена.
   */
  digestEnabled: z.boolean().default(true),
});
export type CompanyNotificationSettingsInput = z.infer<typeof CompanyNotificationSettingsInput>;

export const CompanyNotifications = z.object({
  /** Пояс в ответе — любая строка IANA: BUSINESS_TZ сервера может быть вне списка выбора */
  settings: CompanyNotificationSettingsInput.extend({ timezone: z.string() }),
  email: z.object({ configured: z.boolean(), from: z.string().nullable() }),
  telegram: z.object({
    configured: z.boolean(),
    botUsername: z.string().nullable(),
    webhookOk: z.boolean().nullable(),
  }),
  /** Сколько сотрудников компании получают сводку по каждому каналу */
  recipients: z.object({ email: z.number().int(), telegram: z.number().int(), total: z.number().int() }),
  /** Последние доставки сотрудникам компании, недоставленные — первыми */
  deliveries: z.array(DeliveryView),
});
export type CompanyNotifications = z.infer<typeof CompanyNotifications>;

/* ─────────── Письма «Портал обновлён» после каждой выкладки (АД-09, БЗ-04) ─────────── */

/** Кому компания пишет об обновлении портала */
export const RELEASE_MAIL_MODES = {
  selected: 'Выбранным сотрудникам',
  all: 'Всем сотрудникам компании',
} as const;
export const ReleaseMailMode = z.enum(['selected', 'all']);
export type ReleaseMailMode = z.infer<typeof ReleaseMailMode>;

/**
 * Настройка компании: кому после каждой выкладки приходит письмо «Портал обновлён». Список выбранных хранится
 * и при «всем сотрудникам» — чтобы вернуться к нему одним переключением.
 */
export const ReleaseMailSettings = z.object({
  mode: ReleaseMailMode,
  userIds: z.array(z.uuid()).max(500),
  /**
   * Администраторам компании (владелец и те, кто ведёт её настройки) — без отметки в списке. По умолчанию — да:
   * новая компания сразу получает письма об обновлении у своих администраторов.
   */
  admins: z.boolean(),
});
export type ReleaseMailSettings = z.infer<typeof ReleaseMailSettings>;

/** Сохранение: без отметки «администраторам» остаётся прежняя (у компании без настройки — «да») */
export const ReleaseMailSettingsInput = ReleaseMailSettings.extend({ admins: z.boolean().optional() });
export type ReleaseMailSettingsInput = z.infer<typeof ReleaseMailSettingsInput>;

/** Сотрудник компании в списке выбора */
export const ReleaseMailPerson = z.object({
  userId: z.uuid(),
  fullName: z.string(),
  email: z.string(),
  /** Человек выключил почту в своём профиле — письмо ему не уйдёт, пока он её не включит */
  emailOff: z.boolean(),
  /** Администратор компании: получает письмо по умолчанию, если настройка «администраторам» включена */
  admin: z.boolean(),
});
export type ReleaseMailPerson = z.infer<typeof ReleaseMailPerson>;

export const ReleaseMailAdmin = z.object({
  settings: ReleaseMailSettings,
  /** Действующие сотрудники компании */
  people: z.array(ReleaseMailPerson),
  email: z.object({ configured: z.boolean(), from: z.string().nullable() }),
  /** Демо-компания: внешние каналы выключены, письма не уходят (ГЛ-03) */
  demo: z.boolean(),
  /** Версия, которая работает сейчас */
  commit: z.string(),
  /** Последнее обновление портала, после которого портал писал получателям */
  last: z.object({ commit: z.string(), at: z.string(), newCount: z.number().int() }).nullable(),
  /** Сколько человек получат письмо после следующего обновления при нынешней настройке */
  recipients: z.number().int(),
});
export type ReleaseMailAdmin = z.infer<typeof ReleaseMailAdmin>;

/** Пробное письмо себе: ушло, ждёт повтора или не ушло */
export const ReleaseMailTestResult = z.object({
  status: z.enum(['sent', 'pending', 'failed']),
  error: z.string().nullable(),
  to: z.string(),
});
export type ReleaseMailTestResult = z.infer<typeof ReleaseMailTestResult>;

/* ─────────── Контроль сроков по команде (ЗД-13, раздел 6.21) ─────────── */

/** live — каждому руководителю своё (по умолчанию); draft — все письма одним письмом владельцу; off — выключено */
export const DEADLINE_CONTROL_MODES = {
  off: 'Выключено',
  draft: 'Черновик владельцу компании',
  live: 'Письма руководителям проектов (по умолчанию)',
} as const;
export type DeadlineControlMode = keyof typeof DEADLINE_CONTROL_MODES;

export const DeadlineControlSettings = z.object({
  mode: z.enum(['off', 'draft', 'live']),
  hour: z.union([z.literal(6), z.literal(7), z.literal(8), z.literal(9), z.literal(10), z.literal(11)]),
});
export type DeadlineControlSettings = z.infer<typeof DeadlineControlSettings>;

export const DeadlineControlItem = z.object({
  /** Запись, к которой относится строка: поручение */
  id: z.string(),
  title: z.string(),
  details: z.string().nullable(),
  projectCode: z.string().nullable(),
  due: z.string(),
  daysOverdue: z.number().int(),
  /** Путь записи в портале */
  path: z.string(),
});

/** Письмо руководителю проекта: по проекту — сотрудники команды и их просроченные дела */
export const DeadlineControlLetter = z.object({
  managerId: z.uuid(),
  managerName: z.string(),
  projects: z.array(
    z.object({
      projectId: z.uuid(),
      code: z.string(),
      name: z.string(),
      members: z.array(
        z.object({ userId: z.uuid(), fullName: z.string(), items: z.array(DeadlineControlItem) }),
      ),
    }),
  ),
  itemsCount: z.number().int(),
});
export type DeadlineControlLetter = z.infer<typeof DeadlineControlLetter>;

/** Проект с просрочками, по которому письмо не уйдёт, и почему */
export const DeadlineControlSkip = z.object({
  projectCode: z.string(),
  managerName: z.string(),
  reason: z.string(),
});
export type DeadlineControlSkip = z.infer<typeof DeadlineControlSkip>;

export const DeadlineControlView = z.object({
  settings: DeadlineControlSettings,
  /** Почта портала настроена: без неё письма не уходят */
  mailConfigured: z.boolean(),
  /** Демо-компания: внешние каналы выключены */
  isDemo: z.boolean(),
  /** Дата по часам компании, на которую собраны письма */
  date: z.string(),
  /**
   * Предпросмотр писем — только владельцу компании: в нём дела всех команд всех проектов (черновик тоже уходит
   * только владельцу). Остальным администраторам — только настройки
   */
  previewAllowed: z.boolean(),
  /** Какие письма ушли бы сегодня */
  letters: z.array(DeadlineControlLetter),
  skipped: z.array(DeadlineControlSkip),
  lastRun: z
    .object({
      date: z.string(),
      status: z.string(),
      mode: z.string().nullable(),
      letters: z.number().int(),
      sent: z.number().int(),
      failed: z.number().int(),
      finishedAt: z.string().nullable(),
    })
    .nullable(),
});
export type DeadlineControlView = z.infer<typeof DeadlineControlView>;

/* ─────────── Режим проверки почты компании (УВ-13, раздел 6.20) ─────────── */

/** Сколько проверяющих можно выбрать */
export const MAIL_REVIEW_MAX = 3;

/**
 * Режим проверки: все письма портала по компании уходят проверяющим с пометкой, кому предназначались; приглашения
 * в портал уходят адресату. Проверяющий — сотрудник компании: письма с делами компании наружу не уходят
 */
export const MailReviewSettings = z.object({
  enabled: z.boolean(),
  reviewerIds: z.array(z.string().uuid()).max(MAIL_REVIEW_MAX),
});
export type MailReviewSettings = z.infer<typeof MailReviewSettings>;

export const MailReviewPerson = z.object({
  userId: z.string(),
  fullName: z.string(),
  email: z.string(),
});
export type MailReviewPerson = z.infer<typeof MailReviewPerson>;

export const MailReviewView = z.object({
  settings: MailReviewSettings,
  /** Проверяющие, которым письма уйдут сейчас (действующие сотрудники с почтой) */
  reviewers: z.array(MailReviewPerson),
  /** Кого можно выбрать: действующие сотрудники компании с почтой */
  candidates: z.array(MailReviewPerson),
  mailConfigured: z.boolean(),
  isDemo: z.boolean(),
  /** Последние письма, ушедшие на проверку (новые сверху, до 20) */
  recent: z.array(z.object({ at: z.string(), subject: z.string(), intendedName: z.string() })),
});
export type MailReviewView = z.infer<typeof MailReviewView>;
